import { IconButton, TextButton } from '@creditornot/cb-button';
import { Download, Expand } from '@creditornot/cb-icons';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Item } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { downloadFromUrl } from 'common/utils/download';
import { Offer } from 'modules/shared-menu/pages/SharedMenuEditorPage/types';

import ImageWrapper from '../../ItemDetailsForm/ImageWrapper';
import { getUniqueImageUrls } from '../utils';

type Props = {
  item: Item | Offer;
};

const SingleImageRoot = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const SingleImageWrapper = styled(ImageWrapper)`
  max-width: 320px;
`;

const Image = styled.img`
  display: block;
  width: 100%;
  height: auto;
`;

const DownloadImageButton = styled(TextButton).attrs({ variant: 'blue', size: 'small' })`
  margin-block-start: 8px;
`;

const MultiImageWrapper = styled(ImageWrapper)`
  width: 90px;
`;

const MultiImageRoot = styled.div`
  width: 100%;
`;

const MultiImageRowRoot = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-block-end: 12px;
`;

const MultiImageActions = styled.div`
  display: flex;
  align-items: center;
`;

const handleImageDownload = (image: string) => {
  const downloadURL = `${image}/download`;
  downloadFromUrl(downloadURL, image);
};

const MultiImageRow = ({ imageUrl }: { imageUrl: string }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <MultiImageRowRoot>
      {expanded === false && (
        <>
          <MultiImageWrapper>
            <Image src={imageUrl} />
          </MultiImageWrapper>
          <MultiImageActions>
            <IconButton style={{ marginInlineEnd: '8px' }} variant="grey" size="tiny" onClick={() => setExpanded(true)}>
              <Expand />
            </IconButton>
            <IconButton variant="blue" size="tiny" onClick={() => handleImageDownload(imageUrl)}>
              <Download />
            </IconButton>
          </MultiImageActions>
        </>
      )}
      {expanded === true && (
        <SingleImageRoot>
          <SingleImageWrapper>
            <Image src={imageUrl} />
          </SingleImageWrapper>
          <div>
            <TextButton variant="black" style={{ marginInlineEnd: '8px' }} onClick={() => setExpanded(false)}>
              <FormattedMessage id="state.close" />
            </TextButton>
            <DownloadImageButton onClick={() => handleImageDownload(imageUrl)}>
              <FormattedMessage id="menu-item.download-original-image" />
            </DownloadImageButton>
          </div>
        </SingleImageRoot>
      )}
    </MultiImageRowRoot>
  );
};

const ImageView = ({ item }: Props) => {
  const uniqueImageUrls = getUniqueImageUrls(item);

  if (uniqueImageUrls.length === 1) {
    return (
      <SingleImageRoot>
        <SingleImageWrapper>
          <Image src={uniqueImageUrls[0]} />
        </SingleImageWrapper>
        <DownloadImageButton onClick={() => handleImageDownload(uniqueImageUrls[0])}>
          <FormattedMessage id="menu-item.download-original-image" />
        </DownloadImageButton>
      </SingleImageRoot>
    );
  }

  if (uniqueImageUrls.length > 0) {
    return (
      <MultiImageRoot>
        {uniqueImageUrls.map(url => (
          <MultiImageRow imageUrl={url} key={url} />
        ))}
      </MultiImageRoot>
    );
  }

  return <div>-</div>;
};

export default ImageView;
